import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  GitBranch,
  PieChart,
  TrendingUp,
  ClipboardCheck,
  ChevronDown,
  Check,
  Filter,
  Globe,
} from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { FlagIcon } from '@/components/FlagIcon';
import { PipelineTab } from './components/PipelineTab';
import { BreakdownTab } from './components/BreakdownTab';
import { TimelineTab } from './components/TimelineTab';
import { PlansTab } from './components/PlansTab';
import { usePermissions } from '@/hooks/usePermissions';

const STORAGE_KEY = 'ct-dashboard-state';

const TABS = [
  {
    id: 'pipeline',
    label: 'Pipeline',
    icon: GitBranch,
    tooltip: 'See where campaigns sit in the workflow and track SLA health',
  },
  {
    id: 'breakdown',
    label: 'Breakdown',
    icon: PieChart,
    tooltip: 'Explore how campaigns split across markets, platforms, and formats',
  },
  {
    id: 'timeline',
    label: 'Timeline',
    icon: TrendingUp,
    tooltip: 'Track go-live velocity and activity trends over time',
  },
  {
    id: 'plans',
    label: 'Plans',
    icon: ClipboardCheck,
    tooltip: 'Monitor media plan approvals and MAP import history',
  },
] as const;

type TabId = (typeof TABS)[number]['id'];

const CAMPAIGN_STATUSES = [
  { id: 'active', label: 'Active', description: 'End date is in the future' },
  { id: 'completed', label: 'Completed', description: 'Went live and end date has passed' },
  { id: 'expired', label: 'Expired', description: 'Never went live before end date passed' },
] as const;

type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number]['id'];

const ALL_MARKETS = [
  'PL',
  'IT',
  'NO',
  'NL',
  'CZ',
  'ES',
  'BE',
  'DE',
  'RO',
  'HU',
  'FI',
  'GR',
  'PT',
  'EU',
  'FR',
  'UK',
  'IE',
];

function loadState(): { tab: TabId; statuses: CampaignStatus[]; markets: string[] } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        tab: TABS.some((t) => t.id === parsed.tab) ? parsed.tab : 'pipeline',
        statuses: Array.isArray(parsed.statuses) ? parsed.statuses : ['active'],
        markets: Array.isArray(parsed.markets) ? parsed.markets : [],
      };
    }
  } catch {
    /* ignore */
  }
  return { tab: 'pipeline', statuses: ['active'], markets: [] };
}

function saveState(tab: TabId, statuses: CampaignStatus[], markets: string[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ tab, statuses, markets }));
  } catch {
    /* ignore */
  }
}

export interface DashboardFilters {
  campaignStatus: string;
  market: string;
  animationKey: number;
}

/**
 * Permission gate for the dashboard.
 *
 * Access is backend-driven via `can_view_dashboard` (admins only). Non-admins
 * who navigate directly to /dashboard are redirected home; the backend also
 * rejects the underlying /dashboard endpoints with 403 independently.
 */
export function DashboardPage() {
  const navigate = useNavigate();
  const { data: permissions, isLoading } = usePermissions();
  const canView = permissions?.can_view_dashboard ?? false;

  useEffect(() => {
    if (!isLoading && !canView) {
      navigate({ to: '/content-tracker', replace: true });
    }
  }, [isLoading, canView, navigate]);

  if (isLoading || !canView) {
    return null;
  }

  return <DashboardContent />;
}

function DashboardContent() {
  const initial = loadState();
  const [activeTab, setActiveTab] = useState<TabId>(initial.tab);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<CampaignStatus>>(
    new Set(initial.statuses),
  );
  const [selectedMarkets, setSelectedMarkets] = useState<Set<string>>(new Set(initial.markets));
  const [animationKeys, setAnimationKeys] = useState<Record<TabId, number>>(
    () => Object.fromEntries(TABS.map((t) => [t.id, 0])) as Record<TabId, number>,
  );
  const [statusOpen, setStatusOpen] = useState(false);
  const [marketOpen, setMarketOpen] = useState(false);
  const [marketSearch, setMarketSearch] = useState('');

  const handleTabChange = useCallback((tab: TabId) => {
    setActiveTab(tab);
    setAnimationKeys((prev) => ({ ...prev, [tab]: prev[tab] + 1 }));
  }, []);

  useEffect(() => {
    saveState(activeTab, [...selectedStatuses], [...selectedMarkets]);
  }, [activeTab, selectedStatuses, selectedMarkets]);

  const toggleStatus = useCallback((status: CampaignStatus) => {
    setSelectedStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        if (next.size > 1) next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const toggleMarket = useCallback((market: string) => {
    setSelectedMarkets((prev) => {
      const next = new Set(prev);
      if (next.has(market)) {
        next.delete(market);
      } else {
        next.add(market);
      }
      return next;
    });
  }, []);

  const statusLabel =
    selectedStatuses.size === 3
      ? 'All'
      : [...selectedStatuses]
          .map((s) => CAMPAIGN_STATUSES.find((cs) => cs.id === s)!.label)
          .join(', ');

  const marketLabel =
    selectedMarkets.size === 0
      ? 'All Markets'
      : selectedMarkets.size > 3
        ? `${selectedMarkets.size} markets`
        : null;

  const baseFilters = {
    campaignStatus: [...selectedStatuses].join(','),
    market: [...selectedMarkets].join(','),
  };

  const filteredMarkets = marketSearch
    ? ALL_MARKETS.filter((m) => m.toLowerCase().includes(marketSearch.toLowerCase()))
    : ALL_MARKETS;

  const tabContent: Record<TabId, React.ReactNode> = {
    pipeline: <PipelineTab filters={{ ...baseFilters, animationKey: animationKeys.pipeline }} />,
    breakdown: <BreakdownTab filters={{ ...baseFilters, animationKey: animationKeys.breakdown }} />,
    timeline: <TimelineTab filters={{ ...baseFilters, animationKey: animationKeys.timeline }} />,
    plans: <PlansTab filters={{ ...baseFilters, animationKey: animationKeys.plans }} />,
  };

  return (
    <div className="flex flex-col overflow-hidden" style={{ height: '100vh' }}>
      {/* Header */}
      <div className="flex-shrink-0 bg-white/95 backdrop-blur z-20 relative">
        <Breadcrumb
          title="Dashboard"
          actions={
            <div className="flex items-center flex-1 ml-auto justify-end gap-2">
              {/* Market filter */}
              <div className="relative flex-shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        setMarketOpen(!marketOpen);
                        setStatusOpen(false);
                      }}
                      className="h-8 px-3 text-xs font-medium rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors flex items-center gap-2"
                    >
                      <Globe className="w-3.5 h-3.5 text-slate-400" />
                      {selectedMarkets.size >= 1 && selectedMarkets.size <= 2 ? (
                        <span className="flex items-center gap-1.5">
                          {[...selectedMarkets].map((m) => (
                            <span key={m} className="flex items-center gap-1">
                              <FlagIcon countryCode={m} className="w-3.5 h-auto rounded-[2px]" />
                              {m}
                            </span>
                          ))}
                        </span>
                      ) : selectedMarkets.size === 3 ? (
                        <span className="flex items-center gap-1">
                          {[...selectedMarkets].map((m) => (
                            <FlagIcon
                              key={m}
                              countryCode={m}
                              className="w-3.5 h-auto rounded-[2px]"
                            />
                          ))}
                        </span>
                      ) : (
                        marketLabel
                      )}
                      <ChevronDown
                        className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${marketOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Filter all dashboard data by market</TooltipContent>
                </Tooltip>

                {marketOpen && (
                  <>
                    <button
                      type="button"
                      className="fixed inset-0 z-[50]"
                      onClick={() => setMarketOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-1 z-[60] w-56 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                      <div className="px-3 py-2 border-b border-slate-100">
                        <input
                          type="text"
                          placeholder="Search markets..."
                          value={marketSearch}
                          onChange={(e) => setMarketSearch(e.target.value)}
                          className="w-full text-xs bg-transparent outline-none placeholder:text-slate-400 text-slate-700"
                          autoFocus
                        />
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        {selectedMarkets.size > 0 && (
                          <button
                            onClick={() => setSelectedMarkets(new Set())}
                            className="w-full px-3 py-1.5 text-left text-[11px] font-medium text-[#0014cc] hover:bg-slate-50 border-b border-slate-100"
                          >
                            Clear all
                          </button>
                        )}
                        {filteredMarkets.map((market) => {
                          const isSelected = selectedMarkets.has(market);
                          return (
                            <button
                              key={market}
                              onClick={() => toggleMarket(market)}
                              className="w-full px-3 py-2 text-left hover:bg-slate-50 transition-colors flex items-center gap-2.5"
                            >
                              <div
                                className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                                  isSelected
                                    ? 'bg-[#0014cc] text-white'
                                    : 'border border-slate-300 text-transparent'
                                }`}
                              >
                                <Check className="w-3 h-3" />
                              </div>
                              <FlagIcon countryCode={market} className="w-4 h-auto rounded-[2px]" />
                              <span className="text-sm text-slate-700">{market}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Campaign status filter */}
              <div className="relative flex-shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        setStatusOpen(!statusOpen);
                        setMarketOpen(false);
                      }}
                      className="h-8 px-3 text-xs font-medium rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 transition-colors flex items-center gap-2"
                    >
                      <Filter className="w-3.5 h-3.5 text-slate-400" />
                      {statusLabel}
                      <ChevronDown
                        className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${statusOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    Choose which campaigns to include based on their lifecycle stage
                  </TooltipContent>
                </Tooltip>

                {statusOpen && (
                  <>
                    <button
                      type="button"
                      className="fixed inset-0 z-[50]"
                      onClick={() => setStatusOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-1 z-[60] w-64 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                      <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                          Campaign Lifecycle
                        </p>
                      </div>
                      {CAMPAIGN_STATUSES.map((status) => {
                        const isSelected = selectedStatuses.has(status.id);
                        return (
                          <button
                            key={status.id}
                            onClick={() => toggleStatus(status.id)}
                            className="w-full px-3 py-2.5 text-left hover:bg-slate-50 transition-colors flex items-start gap-2.5 border-b border-slate-50 last:border-b-0"
                          >
                            <div
                              className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                                isSelected
                                  ? 'bg-[#0014cc] text-white'
                                  : 'border border-slate-300 text-transparent'
                              }`}
                            >
                              <Check className="w-3 h-3" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-700">{status.label}</p>
                              <p className="text-[11px] text-slate-400 leading-tight mt-0.5">
                                {status.description}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
          }
        />

        {/* Tabs — absolute centred across the full header width */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none pt-2">
          <div
            className="flex items-end gap-0 pointer-events-auto border-b border-slate-200"
            role="tablist"
          >
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <Tooltip key={tab.id}>
                  <TooltipTrigger asChild>
                    <button
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => handleTabChange(tab.id)}
                      className={`flex items-center gap-1.5 px-4 pb-2.5 pt-1 text-sm font-medium border-b-2 -mb-px transition-all whitespace-nowrap ${
                        isActive
                          ? 'border-[#0014cc] text-[#0014cc]'
                          : 'border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                      {tab.label}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">{tab.tooltip}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab content — all tabs stay mounted, hidden with CSS */}
      <div className="flex-1 overflow-hidden relative">
        {TABS.map((tab) => (
          <div
            key={tab.id}
            role="tabpanel"
            className="absolute inset-0"
            style={{ display: activeTab === tab.id ? 'block' : 'none' }}
          >
            {tabContent[tab.id]}
          </div>
        ))}
      </div>
    </div>
  );
}
